import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import FilterBar from '@components/Common/FilterBar';
import RecordLink from '@components/Common/RecordLink';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import StatTile from '@components/Common/StatTile';
import MintlyTable from '@components/Common/Table';
import { actorPath, signInReasonLabel } from '@constants/activityLabels';
import { getSignInAttemptsQuery, getSignInSummaryQuery } from '@constants/graphQLQueries';
import { formatDateTime } from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { Box, Card, CardContent, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface SignInRow {
  id: string;
  actorId: string | null;
  actorType: string | null;
  actorName: string | null;
  actorEmail: string | null;
  action: string;
  reason: string | null;
  ip: string | null;
  userAgent: string | null;
  timestamp: string | null;
}

interface SignInSummary {
  failed24h: number;
  succeeded24h: number;
  failed7d: number;
  succeeded7d: number;
}

const PAGE_SIZE = 50;
const COLUMNS = ['when', 'result', 'reason', 'account', 'ip', 'browser'];
const RESULT_OPTIONS = [
  { value: '', label: 'Every attempt' },
  { value: 'failed', label: 'Failed only' },
];

const num = (n: number) => n.toLocaleString('en-IN');

/**
 * Control Center sign-in attempts: every staff sign-in and every refused one, with the address it
 * came from. Super Admin only, like the rest of the Activity tab.
 *
 * The rows are written by the backend when the console's server reports an attempt. An address that
 * matched no account is stored only as a hash, so such a row has no name: it reads "Unknown address".
 * Ten failures on one account, or twenty from one address, in fifteen minutes lock it for fifteen
 * minutes and put one alert in every Super Admin's bell, which links here.
 */
const SignInAttempts: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const failedOnly = params.get('result') === 'failed';
  const [draft, setDraft] = useState(failedOnly ? 'failed' : '');

  const [summary, setSummary] = useState<SignInSummary | null>(null);
  const [summaryError, setSummaryError] = useState(false);
  const [rows, setRows] = useState<SignInRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const applyResult = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set('result', value);
    else next.delete('result');
    setParams(next, { replace: true });
    setPage(0);
  };

  useEffect(() => {
    let live = true;
    fetchGraphQLApiData(getSignInSummaryQuery())
      .then((r) => { if (live) setSummary(r?.data?.getSignInSummary ?? null); })
      .catch(() => { if (live) setSummaryError(true); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    fetchGraphQLApiData(getSignInAttemptsQuery(failedOnly, page, PAGE_SIZE))
      .then((r) => {
        if (!live) return;
        setRows(r?.data?.getSignInAttempts?.items ?? []);
        setTotal(r?.data?.getSignInAttempts?.total ?? 0);
      })
      .catch(() => { if (live) setError(true); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [failedOnly, page]);

  // Unknown is a dash, not a zero: a zero is a claim that nobody tried.
  const count = (n: number | undefined) => (summaryError ? '-' : num(n ?? 0));
  const summaryLoading = !summary && !summaryError;

  return (
    <>
      <Box className="cc-stat-grid">
        <StatTile
          label="Failed, last 24 hours"
          value={count(summary?.failed24h)}
          loading={summaryLoading}
          tone={summary && summary.failed24h > 0 ? 'warning' : 'neutral'}
        />
        <StatTile label="Signed in, last 24 hours" value={count(summary?.succeeded24h)} loading={summaryLoading} />
        <StatTile
          label="Failed, last 7 days"
          value={count(summary?.failed7d)}
          loading={summaryLoading}
          tone={summary && summary.failed7d > 0 ? 'warning' : 'neutral'}
        />
        <StatTile label="Signed in, last 7 days" value={count(summary?.succeeded7d)} loading={summaryLoading} />
      </Box>

      <Card variant="outlined" className="cc-panel cc-panel--panel an-panel">
        <CardContent>
          <Typography variant="subtitle1" className="an-chart-title">Sign-in attempts</Typography>
          <FilterBar
            applied={failedOnly ? [{ key: 'result', label: 'Result: Failed only', onClear: () => { setDraft(''); applyResult(''); } }] : []}
            onApply={() => applyResult(draft)}
            onClearAll={() => { setDraft(''); applyResult(''); }}
            onCancel={() => setDraft(failedOnly ? 'failed' : '')}
          >
            <SelectFormField
              required={false}
              name="signInResult"
              label="Result"
              value={draft}
              options={RESULT_OPTIONS}
              onChange={(_n, v) => setDraft(String(v))}
            />
          </FilterBar>

          {loading && rows.length === 0 ? (
            <SkeletonLoader rows={10} columns={COLUMNS.length} />
          ) : error ? (
            <EmptyState compact title="Sign-in attempts could not be loaded" body="Try again in a moment." />
          ) : rows.length === 0 ? (
            <EmptyState compact title={failedOnly ? 'No failed sign-ins recorded' : 'No sign-ins recorded'} />
          ) : (
            <MintlyTable
              pagination={{
                enabled: true,
                page,
                rowsPerPage: PAGE_SIZE,
                totalNumberOfRows: total,
                handleChangePage: (_e, next) => setPage(next),
              }}
              rows={rows}
              selectors={COLUMNS}
              tableHeadRow={{
                when: <>When</>,
                result: <>Result</>,
                reason: <>Reason</>,
                account: <>Account</>,
                ip: <>IP address</>,
                browser: <>Browser</>,
              }}
              tableBodyRow={{
                when: (r: SignInRow) => <>{formatDateTime(r.timestamp, '')}</>,
                result: (r: SignInRow) =>
                  r.action === 'LOGIN'
                    ? <CRMStatusChip status="success" label="Signed in" />
                    : <CRMStatusChip status="failed" label="Failed" />,
                reason: (r: SignInRow) => <>{signInReasonLabel(r.reason)}</>,
                account: (r: SignInRow) => {
                  if (!r.actorId) return <>Unknown address</>;
                  const name = r.actorName || r.actorEmail || r.actorId;
                  const path = actorPath(r.actorType ?? 'admin', r.actorId);
                  return path ? <RecordLink to={path}>{name}</RecordLink> : <>{name}</>;
                },
                ip: (r: SignInRow) => <>{r.ip ?? ''}</>,
                browser: (r: SignInRow) => <span className="an-signin-ua">{r.userAgent ?? ''}</span>,
              }}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default SignInAttempts;
